import {Alert, Button, Paper, SimpleGrid, Stack, Text, Title} from '@mantine/core'
import {parseISO} from 'date-fns'

import {
    AssignmentStripLegend,
    PLANNED_ASSIGNMENT_STRIP_LEGEND_LABELS,
} from 'src/assignments/assignment-strip'

import {RentalAssetTypeFleetUnitCard} from './rental-asset-type-fleet-unit-card'
import type {EquipmentTypeFleet} from './schemas'

type RentalAssetTypeFleetTableProps = {
    fleet: EquipmentTypeFleet | undefined
    isLoading: boolean
    isError: boolean
    errorMessage: string | null
    onRetry: () => void
}

export function RentalAssetTypeFleetTable({
    fleet,
    isLoading,
    isError,
    errorMessage,
    onRetry,
}: RentalAssetTypeFleetTableProps) {
    const referenceDate = fleet ? parseISO(fleet.assignmentWindow.dateFrom) : undefined
    const units = fleet?.units ?? []

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Stack gap={2}>
                    <Title order={4}>Owned Assets</Title>
                    <Text size="sm" c="dimmed">
                        Planned assignments are job reservations for the next 5 days — not field
                        deployment. Deployment badges reflect physical location records.
                    </Text>
                    <AssignmentStripLegend
                        labels={PLANNED_ASSIGNMENT_STRIP_LEGEND_LABELS}
                        presentation="schedule"
                    />
                </Stack>

                {isLoading ? (
                    <Text size="sm" c="dimmed">
                        Loading...
                    </Text>
                ) : isError ? (
                    <Stack gap="sm">
                        <Alert color="red" title="Could not load fleet">
                            {errorMessage ?? 'Something went wrong.'}
                        </Alert>
                        <Button onClick={onRetry} variant="light" size="compact-sm">
                            Retry
                        </Button>
                    </Stack>
                ) : units.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        No fleet units for this type yet.
                    </Text>
                ) : (
                    <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
                        {units.map((unit) =>
                            referenceDate ? (
                                <RentalAssetTypeFleetUnitCard
                                    key={unit.id}
                                    unit={unit}
                                    referenceDate={referenceDate}
                                />
                            ) : null,
                        )}
                    </SimpleGrid>
                )}
            </Stack>
        </Paper>
    )
}
